<template>
  <div 
    ref="triggerRef"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
  >
    <slot />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, defineProps } from 'vue';
import tippy from 'tippy.js';

const PERSIST_HIDE_DELAY_MS = 300;
const TOAST_DISPLAY_DURATION_MS = 2000;
const TOOLTIP_OFFSET = [0, 16];
const PERSISTED_CLASS_NAME = 'sj-tooltip-persisted';

const props = defineProps({
  content: {
    type: String,
    required: true
  },
  placement: {
    type: String,
    default: 'top'
  },
  trigger: {
    type: String,
    default: 'mouseenter focus'
  },
  delay: {
    type: [Number, Array],
    default: 0
  },
  duration: {
    type: [Number, Array],
    default: [300, 250]
  },
  theme: {
    type: String,
    default: 'sj-tooltip'
  },
  arrow: {
    type: Boolean,
    default: true
  },
  interactive: {
    type: Boolean,
    default: false
  },
  hideOnClick: {
    type: Boolean,
    default: true
  },
  persistOnClick: {
    type: Boolean,
    default: false
  }
});

const triggerRef = ref(null);
const tippyInstance = ref(null);
const isTooltipActive = ref(false);
const isPersisted = ref(false);
const persistTimeout = ref(null);
const toastTimeout = ref(null);

// Helper functions
const supportsHover = () => window.matchMedia('(hover: hover)').matches;

const clearAllTimeouts = () => {
  if (persistTimeout.value) {
    clearTimeout(persistTimeout.value);
    persistTimeout.value = null;
  }
  if (toastTimeout.value) {
    clearTimeout(toastTimeout.value);
    toastTimeout.value = null;
  }
};

const addPersistedClass = () => {
  if (tippyInstance.value?.popper) {
    tippyInstance.value.popper.classList.add(PERSISTED_CLASS_NAME);
  }
};

const removePersistedClass = () => {
  if (tippyInstance.value?.popper) {
    tippyInstance.value.popper.classList.remove(PERSISTED_CLASS_NAME);
  }
};

const createTooltipConfig = () => ({
  content: props.content,
  placement: props.placement,
  trigger: supportsHover() ? `${props.trigger} focusin` : 'manual',
  delay: props.delay,
  duration: props.duration,
  theme: props.theme,
  arrow: props.arrow,
  interactive: props.interactive,
  hideOnClick: props.persistOnClick ? false : props.hideOnClick,
  allowHTML: true,
  offset: TOOLTIP_OFFSET,
  ...(props.persistOnClick && createPersistCallbacks())
});

const createPersistCallbacks = () => ({
  onShow: () => {
    isTooltipActive.value = true;
  },
  onHide: () => {
    if (isPersisted.value) {
      return false;
    }
    isTooltipActive.value = false;
  },
  onHidden: () => {
    isPersisted.value = false;
    removePersistedClass();
    if (persistTimeout.value) {
      clearTimeout(persistTimeout.value);
      persistTimeout.value = null;
    }
  }
});

const showToastTooltip = () => {
  if (!tippyInstance.value) return;
  
  tippyInstance.value.show();
  
  if (toastTimeout.value) {
    clearTimeout(toastTimeout.value);
  }
  
  toastTimeout.value = setTimeout(() => {
    tippyInstance.value?.hide();
  }, TOAST_DISPLAY_DURATION_MS);
};

const handleMouseEnter = () => {
  isTooltipActive.value = true;
};

const handleMouseLeave = () => {
  isTooltipActive.value = false;
  
  if (isPersisted.value) {
    persistTimeout.value = setTimeout(() => {
      if (!isTooltipActive.value && isPersisted.value) {
        unpersistTooltip();
      }
    }, PERSIST_HIDE_DELAY_MS);
  }
};

const handlePersistClick = () => {
  if (isTooltipActive.value && tippyInstance.value?.state.isVisible) {
    persistTooltip();
  }
};

const persistTooltip = () => {
  isPersisted.value = true;
  addPersistedClass();
  
  if (persistTimeout.value) {
    clearTimeout(persistTimeout.value);
    persistTimeout.value = null;
  }
};

const unpersistTooltip = () => {
  isPersisted.value = false;
  removePersistedClass();
  tippyInstance.value?.hide();
};

const setupTouchDeviceHandlers = () => {
  triggerRef.value.addEventListener('click', showToastTooltip);
};

const setupPersistHandlers = () => {
  if (!props.persistOnClick || !supportsHover()) return;
  
  triggerRef.value.addEventListener('mouseenter', handleMouseEnter);
  triggerRef.value.addEventListener('mouseleave', handleMouseLeave);
  triggerRef.value.addEventListener('click', handlePersistClick);
};

const initializeTooltip = () => {
  if (!triggerRef.value) return;
  
  tippyInstance.value = tippy(triggerRef.value, createTooltipConfig());
  
  if (!supportsHover()) {
    setupTouchDeviceHandlers();
  }
  
  setupPersistHandlers();
};

// Lifecycle
onMounted(initializeTooltip);

watch(() => props.content, (newContent) => {
  tippyInstance.value?.setContent(newContent);
});

onUnmounted(() => {
  clearAllTimeouts();
  tippyInstance.value?.destroy();
});

// Public API
const updateContent = (newContent) => {
  tippyInstance.value?.setContent(newContent);
};

const showTooltip = () => {
  if (!tippyInstance.value) return;
  tippyInstance.value.show();
};

const hideTooltip = () => {
  if (!tippyInstance.value) return;
  
  isPersisted.value = false;
  removePersistedClass();
  clearTimeout(toastTimeout.value);
  toastTimeout.value = null;
  tippyInstance.value.hide();
};

const handleFocusIn = () => {
  if (!supportsHover() || !tippyInstance.value) return;
  
  // Show tooltip on focus
  tippyInstance.value.show();
  
  if (props.persistOnClick) {
    isTooltipActive.value = true;
  }
};

const handleFocusOut = () => {
  if (!supportsHover() || !tippyInstance.value) return;
  
  // Hide tooltip on focus out unless it's persisted
  if (!isPersisted.value) {
    tippyInstance.value.hide();
  }
  
  if (props.persistOnClick) {
    isTooltipActive.value = false;
    
    if (isPersisted.value) {
      persistTimeout.value = setTimeout(() => {
        if (!isTooltipActive.value && isPersisted.value) {
          unpersistTooltip();
        }
      }, PERSIST_HIDE_DELAY_MS);
    }
  }
};

defineExpose({
  updateContent,
  showTooltip,
  hideTooltip
});
</script>
